import React, { useEffect, useState } from 'react';

interface PerformanceMetrics {
  loadTime: number;
  fileSize: number;
  format: string;
  dimensions: { width: number; height: number };
  compressionRatio: number;
}

interface ImagePerformanceMonitorProps {
  imageUrl: string;
  onMetrics?: (metrics: PerformanceMetrics) => void;
  children: React.ReactNode;
}

export const ImagePerformanceMonitor: React.FC<ImagePerformanceMonitorProps> = ({
  imageUrl,
  onMetrics,
  children
}) => {
  const [metrics, setMetrics] = useState<PerformanceMetrics | null>(null);

  useEffect(() => {
    if (!imageUrl) return;

    const startTime = performance.now();
    let fileSize = 0;
    let format = '';
    let dimensions = { width: 0, height: 0 };

    // Create a test image to measure performance
    const testImg = new Image();
    
    testImg.onload = () => {
      const endTime = performance.now();
      const loadTime = endTime - startTime;
      
      // Get file size from fetch
      fetch(imageUrl, { method: 'HEAD' })
        .then(response => {
          fileSize = parseInt(response.headers.get('content-length') || '0');
          format = response.headers.get('content-type') || '';
          
          const newMetrics: PerformanceMetrics = {
            loadTime,
            fileSize,
            format: format.split('/')[1] || 'unknown',
            dimensions: {
              width: testImg.naturalWidth,
              height: testImg.naturalHeight
            },
            compressionRatio: fileSize > 0 ? (testImg.naturalWidth * testImg.naturalHeight * 3) / fileSize : 0
          };
          
          setMetrics(newMetrics);
          onMetrics?.(newMetrics);
        })
        .catch(() => {
          // Fallback if HEAD request fails
          const newMetrics: PerformanceMetrics = {
            loadTime,
            fileSize: 0,
            format: 'unknown',
            dimensions: {
              width: testImg.naturalWidth,
              height: testImg.naturalHeight
            },
            compressionRatio: 0
          };
          
          setMetrics(newMetrics);
          onMetrics?.(newMetrics);
        });
    };

    testImg.onerror = () => {
      console.warn('Failed to load image for performance monitoring:', imageUrl);
    };

    testImg.src = imageUrl;
  }, [imageUrl, onMetrics]);

  return <>{children}</>;
};

// Hook for tracking Core Web Vitals
export const useImagePerformance = () => {
  const [vitals, setVitals] = useState({
    lcp: 0,
    cls: 0,
    fid: 0
  });

  useEffect(() => {
    // Track Largest Contentful Paint
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1];
      
      if (lastEntry.entryType === 'largest-contentful-paint') {
        setVitals(prev => ({
          ...prev,
          lcp: lastEntry.startTime
        }));
      }
    });

    observer.observe({ entryTypes: ['largest-contentful-paint'] });

    // Track Cumulative Layout Shift
    const clsObserver = new PerformanceObserver((list) => {
      let clsValue = 0;
      for (const entry of list.getEntries()) {
        if (!(entry as any).hadRecentInput) {
          clsValue += (entry as any).value;
        }
      }
      
      setVitals(prev => ({
        ...prev,
        cls: clsValue
      }));
    });

    clsObserver.observe({ entryTypes: ['layout-shift'] });

    return () => {
      observer.disconnect();
      clsObserver.disconnect();
    };
  }, []);

  return vitals;
};

// Performance reporting utility
export const reportImagePerformance = (metrics: PerformanceMetrics, context: string) => {
  // Log to console in development
  if (import.meta.env.DEV) {
    console.log(`📊 Image Performance [${context}]:`, {
      loadTime: `${metrics.loadTime.toFixed(2)}ms`,
      fileSize: `${(metrics.fileSize / 1024).toFixed(2)}KB`,
      format: metrics.format,
      dimensions: `${metrics.dimensions.width}x${metrics.dimensions.height}`,
      compressionRatio: `${metrics.compressionRatio.toFixed(2)}x`
    });
  }

  // Send to analytics in production
  if (import.meta.env.PROD && window.gtag) {
    window.gtag('event', 'image_performance', {
      event_category: 'performance',
      event_label: context,
      value: Math.round(metrics.loadTime),
      custom_map: {
        file_size: metrics.fileSize,
        format: metrics.format,
        compression_ratio: metrics.compressionRatio
      }
    });
  }
};
